import { Component, OnInit } from '@angular/core';
import { ESectionTypes } from '../../../shared/models/eop_types.enum';
import { Icons } from '../../../shared/helpers/icons';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { EopAssistantComponent } from '../../components/eop-assistant/eop-assistant.component';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { DeleteConfirmationComponent } from '../../components/delete-confirmation/delete-confirmation.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { IContact, IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { FormsModule } from '@angular/forms';
import { IBasicResource } from '../../../shared/models/basicResource';
import { ContactsService } from '../../../shared/services/contact.service';
import { BasicResourcesService } from '../../../shared/services/basicResource.service';
import { ResourceDetailComponent } from "./resource-detail/resource-detail.component";
import { EditResourceComponent } from './edit-resource/edit-resource.component';
import { NewResourceComponent } from './new-resource/new-resource.component';

@Component({
  selector: 'basic-resources',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    EopAssistantComponent,
    IntroductionComponent,
    SpinnerComponent,
    PopupMessageComponent,
    PrvNextButtonsComponent,
    DeleteConfirmationComponent,
    NgxPaginationModule,
    ResourceDetailComponent,
    EditResourceComponent,
    NewResourceComponent
],
  templateUrl: './basic-resources.component.html',
  styleUrl: './basic-resources.component.css'
})
export class BasicResourcesComponent implements OnInit {
  eopSectionTypeId = ESectionTypes["List of Materials and Equipment"]

  loading: boolean = false;
  sortOrder: boolean = true;
  showThreatObjectivesDetail: boolean = false;
  showEditThreatObjectives: boolean = false;
  showCreateNewThreatObjectives: boolean = false;
  showDeleteConfirmation: boolean = false;

  icons = Icons;
  searchQuery: string = '';
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  fullResourcesList: IBasicResource[] = [];
  resourcesList: IBasicResource[] = [];
  messageModal: string | null = null;
  popupMessage: string = '';
  selectedResource: any | null = null;
  threatDetails: IBasicResource | null = null;
  materialToEdit: IBasicResource | null = null;
  page = 1;
  itemsPerPage = 10;
  fullContactsList: IContact[] = [];
  showModalType: string = '';

  constructor(
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private basicResourcesService: BasicResourcesService

  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadContacts();
  }  

  // load all Contacts and Contacts of a department
  loadContacts(): void {
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loading = false;
        this.fullContactsList = response.data || [];
        console.log('Contacts loaded successfully:', response);
        this.loadMetarialsList();
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  };

  loadMetarialsList(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.basicResourcesService.getBuildingBasicResources(this.currentBuildingId).subscribe({
        next: (res) => {
          console.log('Resources loaded successfully:', res);
          if (!res.has_error) {
            this.fullResourcesList = res.data;// Map materials + attach contact_name
            this.resourcesList = this.fullResourcesList.map((material: any) => {
              const contact = this.fullContactsList.find(c => c.id === material.contact_id);
              return {
                ...material,
                contact_name: contact ? contact.name : 'N/A'
              };
            });
          }
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message || 'Failed to load basic resources. Please try again.';
        }
      })
    }
  }

  // sort threat-goal added to header of table
  sortList(key: keyof typeof this.resourcesList[0]): void {
    this.sortOrder = !this.sortOrder;
    this.resourcesList.sort((a, b) => {
      const valueA = a[key]?.toString().toLowerCase() || '';
      const valueB = b[key]?.toString().toLowerCase() || '';

      if (valueA < valueB) {
        return this.sortOrder ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortOrder ? 1 : -1;
      }
      return 0;
    });
  }

  handleOpenModal(modalName: string, selectedItem?: any): void {
    this.showModalType = modalName;
    this.selectedResource = selectedItem ? selectedItem : null;
  }

  handleCloseModal(modalName?: string): void {
    this.showModalType = '';
    this.selectedResource = null;
  }

  handleSave(message: {type: string, message: string}, actionType: string): void {
    this.handleCloseModal(actionType);
    this.loadMetarialsList();

    if (message.type === 'success') {
      this.messageModal = 'success';
    } else if (message.type === 'unsuccess') {
      this.messageModal = 'unsuccess';
    } 
    this.popupMessage = message.message;
  }

  approveResources(): void {
    if (this.currentBuildingId) {
      this.basicResourcesService.approveBuildingBasicResources(this.currentBuildingId).subscribe({
        next: (res) => {
          if (!res.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after confirming list of materials & equipment.';
            this.loadMetarialsList();
          }
        },
        error: (err) => {
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      })
    }
  }

  handleDelete(item: any): void {
    this.loading = true;
    if (this.currentBuildingId && item.id) {
      this.basicResourcesService.deleteResource(this.currentBuildingId, item.id.toString()).subscribe({
        next: (res) => {
          this.loading = false;
          this.showModalType = '';
          if (!res.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after deleting a material & equipment.';
            this.loadMetarialsList();
          }
        },
        error: (err) => {
          this.loading = false;
          this.showModalType = '';
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      })
    }
  } 

  filterResourcesList(): void {
    const query = this.searchQuery.toLowerCase().trim();
  
    this.resourcesList = this.fullResourcesList.filter((item) => {
      return (
        item.resources.toLowerCase().includes(query) ||
        item.resource_group.toLowerCase().includes(query) ||
        item.location.toLowerCase().includes(query) ||
        item.last_updated_user_name?.toLowerCase().includes(query) ||
        item.last_updated_at?.toLowerCase().includes(query) ||
        item.contact_name?.toString().toLowerCase().includes(query)
      );
    });
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }

}
